/**
 * Aritmetica su date-calendario, senza fusi e senza ore.
 *
 * Otto ragiona in *giorni di calendario* (una notte = un giorno), mai in istanti.
 * Rappresentiamo una data come `YYYY-MM-DD` e facciamo i conti su numeri di
 * giorno interi: così l'ora legale non può spostare un intervallo di una notte,
 * che è il classico bug da 20€ sbagliati su una prenotazione.
 */

export type IsoDate = string;

export const TIMEZONE = "Europe/Rome";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== "string" || !ISO_DATE_RE.test(value)) return false;
  // Rifiuta date sintatticamente valide ma inesistenti (2026-02-30).
  return fromDayNumber(toDayNumber(value)) === value;
}

export function assertIsoDate(value: unknown, label = "data"): IsoDate {
  if (!isIsoDate(value))
    throw new TypeError(`${label} non valida: ${String(value)}`);
  return value;
}

/** Giorni trascorsi dal 1970-01-01 (può essere negativo). */
export function toDayNumber(date: IsoDate): number {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  return Math.floor(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

export function fromDayNumber(dayNumber: number): IsoDate {
  const d = new Date(dayNumber * MS_PER_DAY);
  const year = String(d.getUTCFullYear()).padStart(4, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return fromDayNumber(toDayNumber(date) + days);
}

/**
 * Il mese in corso: dal primo all'ultimo giorno del mese di `oggi`.
 *
 * Finché un mese non è finito, l'host guarda **quello** — ed è quello che il
 * gestionale gli mette davanti. Una finestra mobile di trenta giorni risponde
 * a un'altra domanda («com'è pieno da qui in avanti»), e messe vicine le due
 * risposte si leggono come un errore: San Maurizio al 100% su Guesty e al 53%
 * su Otto erano due conti giusti su due periodi diversi.
 */
export function meseInCorso(oggi: IsoDate = todayInRome()): {
  da: IsoDate;
  a: IsoDate;
} {
  const anno = Number(oggi.slice(0, 4));
  const mese = Number(oggi.slice(5, 7));
  // Il giorno 0 del mese dopo è l'ultimo di questo: niente tabelle di 30/31 e
  // niente casi particolari per febbraio bisestile.
  const ultimo = new Date(Date.UTC(anno, mese, 0)).getUTCDate();
  const prefisso = oggi.slice(0, 7);
  return {
    da: `${prefisso}-01`,
    a: `${prefisso}-${String(ultimo).padStart(2, "0")}`,
  };
}

/** Numero di notti in un intervallo con entrambi gli estremi inclusi. */
export function nightsInRange(from: IsoDate, to: IsoDate): number {
  return toDayNumber(to) - toDayNumber(from) + 1;
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minDate(a: IsoDate, b: IsoDate): IsoDate {
  return a <= b ? a : b;
}

export function maxDate(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b;
}

export function isBetween(date: IsoDate, from: IsoDate, to: IsoDate): boolean {
  return date >= from && date <= to;
}

/** Tutte le date dell'intervallo, estremi inclusi. */
export function eachDate(from: IsoDate, to: IsoDate): IsoDate[] {
  const start = toDayNumber(from);
  const end = toDayNumber(to);
  if (end < start) return [];
  const out: IsoDate[] = new Array(end - start + 1);
  for (let n = start; n <= end; n++) out[n - start] = fromDayNumber(n);
  return out;
}

/** 0 = domenica … 6 = sabato. Il 1970-01-01 era un giovedì. */
export function weekday(date: IsoDate): number {
  return (((toDayNumber(date) + 4) % 7) + 7) % 7;
}

export function isWeekend(date: IsoDate): boolean {
  const d = weekday(date);
  return d === 0 || d === 5 || d === 6; // ven/sab/dom: il "weekend" di chi affitta
}

/** Oggi secondo il fuso italiano — l'unico "adesso" che il prodotto conosce. */
export function todayInRome(now: Date = new Date()): IsoDate {
  return giornoInRome(now);
}

// ── Da istante a giorno italiano ─────────────────────────────────────────────

/**
 * Un istante, spezzato nei pezzi che si leggono a Roma.
 *
 * Serve perché `Date.toISOString()` parla UTC, e in Italia UTC è indietro di
 * una o due ore: la scadenza delle 00:00 del 15 settembre viaggia come
 * `2026-09-14T22:00:00Z`, e un `slice(0, 10)` su quella stringa la sposta al
 * 14. Un giorno di calendario sbagliato su una pulizia è un avviso falso.
 */
const PEZZI_ROMA = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

interface PezziRoma {
  anno: string;
  mese: string;
  giorno: string;
  ora: string;
  minuto: string;
  secondo: string;
}

function pezziRoma(istante: Date): PezziRoma {
  const p = new Map(
    PEZZI_ROMA.formatToParts(istante).map((x) => [x.type, x.value]),
  );
  return {
    anno: p.get("year")!,
    mese: p.get("month")!,
    giorno: p.get("day")!,
    ora: p.get("hour")!,
    minuto: p.get("minute")!,
    secondo: p.get("second")!,
  };
}

/** Di quanti minuti l'Italia è avanti rispetto a UTC in quell'istante (60 o 120). */
export function offsetMinutiRoma(istante: Date): number {
  const p = pezziRoma(istante);
  const comeSeFosseUtc = Date.UTC(
    Number(p.anno),
    Number(p.mese) - 1,
    Number(p.giorno),
    Number(p.ora),
    Number(p.minuto),
    Number(p.secondo),
  );
  return Math.round((comeSeFosseUtc - istante.getTime()) / 60_000);
}

/** Il giorno di calendario italiano di un istante. */
export function giornoInRome(istante: Date): IsoDate {
  const p = pezziRoma(istante);
  return `${p.anno}-${p.mese}-${p.giorno}`;
}

/**
 * Lo stesso istante, scritto con l'offset italiano invece che con la `Z`.
 *
 * Due proprietà, tutte e due necessarie: `slice(0, 10)` dà il giorno italiano,
 * e `new Date(...)` rilegge l'istante esatto. Una data-ora che viaggia così
 * non può essere letta di un giorno indietro da chi la guarda a fette.
 */
export function isoInRome(istante: Date): string {
  const p = pezziRoma(istante);
  const offset = offsetMinutiRoma(istante);
  const segno = offset < 0 ? "-" : "+";
  const assoluto = Math.abs(offset);
  const ore = String(Math.floor(assoluto / 60)).padStart(2, "0");
  const minuti = String(assoluto % 60).padStart(2, "0");
  return `${p.anno}-${p.mese}-${p.giorno}T${p.ora}:${p.minuto}:${p.secondo}${segno}${ore}:${minuti}`;
}

/**
 * L'istante in cui, in Italia, comincia (o finisce) un giorno di calendario.
 *
 * Il giro è in due passate perché l'offset dipende dall'istante che stiamo
 * cercando: si parte dall'ora UTC, si guarda l'offset lì, si corregge e si
 * ricontrolla. Sbaglia solo nell'ora che l'ora legale cancella, dove
 * mezzanotte esiste comunque.
 */
export function istanteInRome(
  giorno: IsoDate,
  ora: string = "00:00:00.000",
): Date {
  const comeUtc = Date.parse(`${giorno}T${ora}Z`);
  const primoTentativo = new Date(
    comeUtc - offsetMinutiRoma(new Date(comeUtc)) * 60_000,
  );
  return new Date(comeUtc - offsetMinutiRoma(primoTentativo) * 60_000);
}

/** L'istante in cui comincia il giorno italiano. */
export function inizioGiornoRoma(giorno: IsoDate): Date {
  return istanteInRome(giorno, "00:00:00.000");
}

/** L'ultimo istante del giorno italiano. */
export function fineGiornoRoma(giorno: IsoDate): Date {
  return istanteInRome(giorno, "23:59:59.999");
}

/** Spezza un elenco di date sparse in intervalli contigui. */
export function toContiguousRanges(
  dates: readonly IsoDate[],
): Array<{ from: IsoDate; to: IsoDate }> {
  if (dates.length === 0) return [];
  const sorted = [...new Set(dates)].sort();
  const ranges: Array<{ from: IsoDate; to: IsoDate }> = [];
  let start = sorted[0]!;
  let prev = start;
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i]!;
    if (toDayNumber(current) === toDayNumber(prev) + 1) {
      prev = current;
      continue;
    }
    ranges.push({ from: start, to: prev });
    start = current;
    prev = current;
  }
  ranges.push({ from: start, to: prev });
  return ranges;
}

// ── Formattazione in italiano ────────────────────────────────────────────────

const DAY_MONTH = new Intl.DateTimeFormat("it-IT", {
  day: "numeric",
  month: "long",
  timeZone: "UTC",
});
const DAY_MONTH_YEAR = new Intl.DateTimeFormat("it-IT", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const WEEKDAY_SHORT = new Intl.DateTimeFormat("it-IT", {
  weekday: "short",
  timeZone: "UTC",
});

function asUtcDate(date: IsoDate): Date {
  return new Date(toDayNumber(date) * MS_PER_DAY);
}

/** "12 settembre" — o "12 settembre 2027" se l'anno non è quello corrente. */
export function formatDate(
  date: IsoDate,
  today: IsoDate = todayInRome(),
): string {
  const sameYear = date.slice(0, 4) === today.slice(0, 4);
  return (sameYear ? DAY_MONTH : DAY_MONTH_YEAR).format(asUtcDate(date));
}

export function formatWeekday(date: IsoDate): string {
  return WEEKDAY_SHORT.format(asUtcDate(date)).replace(".", "");
}

/** "dal 12 al 25 settembre", "il 12 settembre", "dal 28 settembre al 3 ottobre". */
export function formatRange(
  from: IsoDate,
  to: IsoDate,
  today: IsoDate = todayInRome(),
): string {
  if (from === to) return `il ${formatDate(from, today)}`;
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  if (sameMonth) {
    const day = String(Number(from.slice(8, 10)));
    return `dal ${day} al ${formatDate(to, today)}`;
  }
  return `dal ${formatDate(from, today)} al ${formatDate(to, today)}`;
}

/** "16 notti" / "1 notte". */
export function formatNights(count: number): string {
  return `${count} ${count === 1 ? "notte" : "notti"}`;
}
